import { DataRange } from '@/client/reports';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { FlexContainer } from '@/ui/styled-ui';
import { formatDate } from '@/utils/date/date.helper';
import { Dialog, DialogProps } from 'primereact/dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type RangeSelectorProps = {
  dataRange: DataRange;
  setDataRange: React.Dispatch<React.SetStateAction<DataRange>>;
} & DialogProps;

export const RangeSelector: React.FC<RangeSelectorProps> = ({
  onHide,
  visible,
  dataRange,
  setDataRange,
  ...dialogProps
}) => {
  const { t } = useTranslation();
  const [datePickerValue, setDatePickerValue] = useState<Date[] | null>(
    dataRange.value.from && dataRange.value.to
      ? [dataRange.value.from, dataRange.value.to]
      : [],
  );

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{t('dashboard.dataRange.selectRange')}</h1>}
      onHide={onHide}
      draggable={false}
      {...dialogProps}
    >
      <FlexContainer
        direction="column"
        justify="flex-start"
        align="flex-start"
        className="mb-4"
        gap={8}
      >
        <label htmlFor="datePicker">
          {t('dashboard.dataRange.customRange')}:
        </label>
        <AppCalendar
          className="w-full"
          value={datePickerValue}
          onChange={(event: any) => {
            setDatePickerValue(event.value);
          }}
          selectionMode="range"
        />
      </FlexContainer>

      <FlexContainer justify="flex-end" className="mt-5">
        <AppButton
          label={t('button.cancel')}
          severity="secondary"
          type="outlined"
          onClick={onHide}
          className="mr-3"
        />
        <AppButton
          label={t('button.apply')}
          onClick={() => {
            const fromDate = datePickerValue ? datePickerValue[0] : undefined;
            const toDate = datePickerValue ? datePickerValue[1] : undefined;

            const customLabel =
              fromDate && toDate
                ? `${t('dashboard.dataRange.customRange')} (${formatDate(
                    fromDate,
                    DateFormats.DATEONLY,
                  )} - ${formatDate(toDate, DateFormats.DATEONLY)})`
                : `${t('dashboard.dataRange.customRange')}:`;

            setDataRange({
              ...dataRange,
              label: customLabel,
              value: {
                from: fromDate,
                to: toDate,
              },
            });
            onHide();
          }}
          isDisabled={!datePickerValue}
        />
      </FlexContainer>
    </StyledDialog>
  );
};
